import { Injectable } from '@angular/core';
import { Delivery, DeliveryForm, DeliveryList, SingleDelivery, WebserviceResponse } from "../interfaces";
import { HttpClient, HttpErrorResponse, HttpParams } from "@angular/common/http";
import { AlertService, IAlertType } from "./alert.service";
import { Observable, throwError } from "rxjs";
import { catchError, map, retry } from "rxjs/operators";
import { normalizeSingletonResponse } from "../utils/response";
import { restBaseUrl } from "../utils/rest-url";

@Injectable({
  providedIn: 'root'
})
export class DeliveryStoreService {
  delivery: Delivery | null = null;

  private readonly endpoint = restBaseUrl('/delivery');

  constructor(
      private http: HttpClient,
      private alertDispatcher: AlertService
  ) { }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Unknown error!';
    if (error.error instanceof ErrorEvent) {
      // Client-side errors
      errorMessage = `Client-side error: ${error.error.message}`;
    } else {
      // Server-side errors
      errorMessage = `Server-side error code: ${error.status} - message: ${error.message}`;
    }
    // window.alert(errorMessage);

    this.alertDispatcher.show({
      message: errorMessage,
      clrAlertType: IAlertType.DANGER
    }, 5000);

    // return throwError(errorMessage); deprecated
    return throwError(() => new Error(errorMessage));
  }

  // Main CRUD Services

  public getDelivery(projectnumber:string, deliveryid: number, jobid: number, state:string): Observable<WebserviceResponse<Delivery[]>> {
    const options = new HttpParams({
      fromObject: {
        project_id: projectnumber,
        delivery_id: deliveryid,
        job_id: jobid,
        status: state
      }
    });
    return this.http.get<WebserviceResponse<Delivery[]>>(
        this.endpoint + '/get_delivery',
        { params: options }
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  // Main CRUD Services

  public getSingleDelivery(projectId: string, deliveryId: number, state: string): Observable<WebserviceResponse<SingleDelivery>> {
    const options = new HttpParams({
      fromObject: {
        project_id: projectId,
        delivery_id: deliveryId,
        job_id: 0,
        status: state
      }
    });
    return this.http.get<WebserviceResponse<SingleDelivery>>(
      this.endpoint + '/get_delivery',
      { params: options }
    ).pipe(
      map((response) => normalizeSingletonResponse(response)),
      retry(3),
      catchError(this.handleError.bind(this))
    );
  }

  public updateDelivery(del: DeliveryForm, jobid: number) {
    return this.http.put(
        `${this.endpoint}/update_delivery?job_id=${jobid}`,
        del,
        { responseType: 'json'}

    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public createDeliveryNote(prid: string, jobid: number, date: string, warehouseIds?: number[]): Observable<WebserviceResponse<boolean>> {
    const url = `${this.endpoint}/add_delivery?job_id=${jobid}&project_id=${prid}&delivery_date=${date}`;
    const body = warehouseIds && warehouseIds.length > 0 ? { warehouse_ids: warehouseIds } : null;
    return this.http.post<WebserviceResponse<boolean>>(url, body).pipe(
      retry(3),
      catchError(this.handleError.bind(this))
    );
  }

  public deleteDeliveryNote(projectnumber: string, deliveryid: number ){
    return this.http.delete(
        `${this.endpoint}/delete_delivery?delivery_id=${deliveryid}&project_id=${projectnumber}`,
        { responseType: 'text'}

    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  // Delivery Note Position Services

  public getDeliveryPositions(projectnumber: string, jobID: number, deliveryID: number, positionID: string, partID: string): Observable<WebserviceResponse<Delivery[]>> {
    const options = new HttpParams({
      fromObject: {
        project_id: projectnumber,
        job_id: jobID,
        delivery_id: deliveryID,
        position: positionID,
        part: partID
      }
    });
    return this.http.get<WebserviceResponse<Delivery[]>>(
        this.endpoint + '/get_delivery_position',
        { params: options }
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public updateDeliveriesPosition(delivery: DeliveryList): Observable<WebserviceResponse<Delivery>> {
    return this.http.put<WebserviceResponse<Delivery>>(
        `${this.endpoint}/update_delivery_position`,
        delivery
    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  public deleteDeliveryPosition(projectnumber: string, deliveryID: number, positionID: string, partID: number) {
    return this.http.delete(
        `${this.endpoint}/delete_delivery_position?project_id=${projectnumber}&delivery_id=${deliveryID}&position=${positionID}&part=${partID}`,
        { responseType: 'text'}

    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }

  // Delivery Status

  public updateDeliveryStatus(projectID: string, deliveryID: number, status: string){
    return this.http.put(
        `${this.endpoint}/update_status?project_id=${projectID}&delivery_id=${deliveryID}&status=${status}`,
        { responseType: 'text'}

    ).pipe(
        retry(3),
        catchError(this.handleError.bind(this))
    );
  }
}
